import styles from 'views/ProductPage/configurators/variant/themes/BGH/styles.scss';
import { ToolTipDialog } from 'components/Shared/ToolTip/themes/BGH-RD3';
import { NO_ELEMENT, rm } from 'utils';
import { Attribute, useConfigurator } from 'views/ProductPage/configurators/useConfigurator';
import {
  AttributeHoverPayload,
  useImageAttributeContext,
  AttributeProductImagePayload
} from 'views/ProductPage/configurators/imageAttributeContext';
import { BhImage } from 'models/BhImage';
import Image from 'components/Shared/Image';
import cn from 'classnames';
import { AttributeOptionValue } from 'models/AttributeOption';
import { useCallback } from 'react';
import connect from 'components/Shared/connect';
import { getProductImage } from 'views/ProductPage/selector';
import ownStyles from './style.scss';
import { LoadingStatus } from 'models/LoadingStatus';

type AttributeImageValue = Required<AttributeOptionValue>;
export type AttributeImageOption = Omit<Attribute, 'values'> & {
  values: AttributeImageValue[];
};

interface Props extends AttributeImageOption {
  isMaster: (id: string) => boolean;
  hasMasterConstraint: boolean;
}

interface MergedProps extends Props {
  imageFallback?: BhImage;
  loading: boolean;
}

type ImageAttributeProps = {
  active: boolean;
  disabled: boolean;
  triggerBigImage: boolean;
  onClick: () => void;
  image?: BhImage;
} & AttributeHoverPayload &
  AttributeProductImagePayload;

const ImageAttribute = ({
  displayName,
  description,
  triggerBigImage,
  productImage,
  onClick,
  active,
  disabled,
  hoverImage
}: ImageAttributeProps) => {
  const { setProductImage, hover, resetHover } = useImageAttributeContext();

  const onMouseOver = useCallback(() => {
    hover({ hoverImage, displayName, description });
  }, [hover, hoverImage, displayName, description]);

  const selectAttribute = () => {
    if (triggerBigImage && productImage) {
      setProductImage({ productImage });
    }
    onClick();
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      selectAttribute();
    }
  };

  return (
    <li
      onClick={selectAttribute}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className={cn(styles.item, {
        [styles.active]: active,
        [styles.diagonalDash]: disabled
      })}
    >
      <Image
        {...{
          ...hoverImage,
          onMouseOver,
          onMouseOut: resetHover,
          height: 70,
          width: 70,
          paddingBottom: true,
          className: cn({ [styles.disabled]: disabled })
        }}
      />
    </li>
  );
};

const _AttributeImage = ({ loading, hasMasterConstraint, isMaster, ...a }: MergedProps) => {
  const { onSelectChange } = useConfigurator();
  const selectedValue = a.values.find(v => v.value === a.selected);

  return (
    <div className={cn(ownStyles.attributeImage, { [ownStyles.blockPointerEvents]: loading })}>
      <div className={ownStyles.label}>
        {!a.selected && <span className={ownStyles.choose}>{rm('product_variant_choose')} </span>}
        {a.name}
        {selectedValue && <span className={ownStyles.selected}>: {selectedValue.label}</span>}
        {a.toolTip && <ToolTipDialog className={ownStyles.toolTip} text={a.toolTip} />}
      </div>

      <ul className={styles.selectImage}>
        {a.values.map(({ image, disabled, label, value, description, ...v }) => {
          if (hasMasterConstraint && disabled && !isMaster(a.id)) {
            return NO_ELEMENT;
          }
          const productImage = v.productImage || a.imageFallback || {};
          const hoverImage = image || a.imageFallback || {};

          const click = () => {
            onSelectChange(a.id, value, {
              emit: true,
              setSelectedOnReset: hasMasterConstraint
            });
          };

          return (
            <ImageAttribute
              key={value}
              onClick={click}
              description={description || a.description}
              disabled={disabled}
              displayName={label}
              hoverImage={hoverImage}
              productImage={productImage}
              active={Boolean(a.selected === value)}
              triggerBigImage={a.triggerBigImage || hasMasterConstraint}
            />
          );
        })}
      </ul>
    </div>
  );
};

export const AttributeImage = connect<MergedProps>(({ currentPage }) => ({
  imageFallback: getProductImage(currentPage),
  loading: currentPage.status === LoadingStatus.Fetching
}))(_AttributeImage);
